const ApiMethods = <const>[
  'addDeviceOrientationListener',
  'setStudioData',
  'setStartScreenVisibility',
  'setSoundControlVisibility',
  'setProgressControlVisibility',
  'setFullscreenButtonVisibility',
  'setFixedBackgroundImage',
  'hideVideoContainer',
  'setBackgroundColor',
  'sendAdClickThru',
  'requestFullscreen',
  'performTapMode',
  'sendVideoMetadata',
  'hideCredits',
  'requestInterscroller',
];

const ReturningApiMethods = <const>[
  'getContextData',
  'getSlotBounds',
  'isStudioDisplay',
  'isReplay',
  'getUserLocation',
  'getTeadsInfo',
  'getSumologicOption',
  'getStudioData',
  'isFullscreen',
  'customizeUI',
  'isMobile',
];

function postMessageApi(w: Window) {
  const api = {};
  for (const method of ApiMethods) {
    Object.defineProperty(api, method, {
      value: (...methodArgs: unknown[]) => w.postMessage({ method, methodArgs }, '*'),
    });
  }
  for (const method of ReturningApiMethods) {
    Object.defineProperty(api, method, {
      value: (ret: Function, ...methodArgs: unknown[]) => {
        addEventListener('message', function cb({ data }: MessageEvent) {
          if ('method' in data) {
            const { method: replyMethod, error, methodReturn } = data;
            if (method && replyMethod === method) {
              removeEventListener('message', cb);
              ret && ret(error, methodReturn);
            }
          }
        });
        w.postMessage({ method, methodArgs }, '*');
      },
    });
  }
  return api;
}

function wrapApi(w: any) {
  const api = {};
  for (const method of ApiMethods) {
    Object.defineProperty(api, method, {
      value: (...args: unknown[]) => w?.teads?.api?.[method](...args),
    });
  }
  for (const method of ReturningApiMethods) {
    Object.defineProperty(api, method, {
      value: (ret: Function, ...methodArgs: unknown[]) => {
        try {
          ret(null, w?.teads?.api?.[method](...methodArgs));
        } catch (err) {
          console.warn(`[teads.api] ${method}`, err);
          ret(err);
        }
      },
    });
  }
  return api;
}

const HandshakeMethod = 'studio-ad--format-RPC';
const Retries = 5;
const Timeout_ms = 50;

function getWindows(w: Window = window) {
  const windows = [];
  while (w !== w.parent) {
    w = w.parent;
    windows.push(w);
  }
  return windows;
}

function sendInitRequest() {
  if (instance) return;
  for (const w of getWindows()) {
    w.postMessage({ method: HandshakeMethod }, '*');
  }
}

function findTeadsApiInWindow() {
  if ('teads' in window) return window;
  for (const w of getWindows()) {
    try {
      if ('teads' in w) return w;
    } catch (e) {
      // i'm sorry this ruined your day
    }
  }
}

let instance: any | null = null;

let apiQueue = (_: any) => {};

let findingApi = false;

function findTeadsApi() {
  const w = findTeadsApiInWindow();
  if (w) {
    instance = w && wrapApi(w);
    onTeadsApiAvailable.applyChange(true);
  }
  setTimeout(
    () => {
      if (!instance) {
        onTeadsApiAvailable.applyChange(false);
      }
    },
    (Retries + 1) * Timeout_ms
  );
  if (instance !== null) {
    apiQueue(instance);
  } else {
    addEventListener('message', function onMessage({ data, source }: MessageEvent) {
      if (data && data.method == HandshakeMethod) {
        removeEventListener('message', onMessage);
        instance = postMessageApi(source as Window);
        apiQueue(instance);

        onTeadsApiAvailable.applyChange(true);
      }
    });
    sendInitRequest();
    for (let i = 1; i < Retries; i++) {
      setTimeout(sendInitRequest, i * Timeout_ms);
    }
  }
}

function enqueue(fn: Function) {
  if (instance) {
    fn(instance);
  } else {
    const prev = apiQueue;
    apiQueue = function (instance) {
      prev(instance);
      fn(instance);
    };
    if (!findingApi) {
      findingApi = true;
      findTeadsApi();
    }
  }
}

type TeadsApi = {
  [method in (typeof ApiMethods)[number]]: (...args: any[]) => void;
} & {
  [method in (typeof ReturningApiMethods)[number]]: (cb: (err: Error, value: any) => void, ...args: any[]) => void;
};

export const teadsApi = {} as TeadsApi;

for (const name of ApiMethods) {
  Object.defineProperty(teadsApi, name, {
    value: (...args: unknown[]) => {
      return enqueue((api: TeadsApi) => api[name](...args));
    },
  });
}
for (const name of ReturningApiMethods) {
  Object.defineProperty(teadsApi, name, {
    value: (cb: Function, ...args: unknown[]) => {
      enqueue((api: TeadsApi) =>
        api[name](
          (...args: unknown[]) => {
            return cb(...args);
          },
          ...args
        )
      );
    },
  });
}

let lastVideoMetadata: { width: number; height: number } | null = null;

export function sendVideoMetadata(size: { width: number; height: number }) {
  lastVideoMetadata = size;
  teadsApi.sendVideoMetadata(size);
}

export function getLastVideoMetadata() {
  return lastVideoMetadata && { ...lastVideoMetadata };
}

export const onTeadsApiAvailable = {
  value: null as null | boolean,
  subs: new Array<{ (available: boolean): void }>(),
  subscribe: (fn: { (available: boolean): void }) => {
    onTeadsApiAvailable.subs.push(fn);
    if (onTeadsApiAvailable.value != null) fn(onTeadsApiAvailable.value);
  },
  unsubscribe: (fn: { (available: boolean): void }) => {
    onTeadsApiAvailable.subs = onTeadsApiAvailable.subs.filter((sub) => sub !== fn);
  },
  applyChange: (available: boolean) => {
    onTeadsApiAvailable.value = available;
    onTeadsApiAvailable.subs.forEach((sub) => {
      try {
        sub(available);
      } catch (e) {
        console.error(e);
      }
    });
  },
};
